import {Price} from "../apps/components/PriceLabel";
import {RequestStep} from "../enums/RequestStep";
import {Promo} from "../enums/Promo";
import {Analytics} from "./Analytics";
import {translationModel, Translations} from "../apps/request/RequestTranslationModel";
import { HttpError } from "../utils/_http-error";

type RequestSummaryCost = {
    value: string;
    currency: string;
    prefix?: string;
    denominator?: string;
};

export type RequestSummary = {
    cost: RequestSummaryCost;
    hint?: RequestSummaryCost;
    name?: string;
};

export type Service = {
    id: number;
    name: string;
    url: string;
    hasQuantity: boolean;
    maxQuantity?: number;
    minQuantity?: number;
    options?: Option[];
    optionsName?: string;
    optionsTitle?: string;
};

export type Option = {
    id: number | string;
    name: string;
    description?: string;
    hasQuantity?: boolean;
    maxQuantity?: number;
    minQuantity?: number;
    price?: Price;
}

export type FormErrors = {
    [key: string]: string[]
}

export type HandleStepParams = {
    step : RequestStep;
    requestId?: string;
    _visitor?: string,
}

type HandleStepResponse = {
    success: boolean;
    redirectUrl: string | null;
    requestId: string;
    errors: FormErrors;
}

export type RequestForms = Record<RequestStep, RequestForm>;

export type RequestFormsResponse = {
    forms: RequestForms;
    translations: Translations;
};

export type RequestForm = {
    data: Record<string, InputValue>;
    options: Record<string, FormOption[]>;
}

type FormOption = {
    id: string | number;
    text: string;
    hint?: string;
    price?: {
        value: string;
        currency: string;
        denominator: string;
    };
}

export type InputValue = [number, string, number[], string[]] | null;

export type SendScholarshipRewardRequest = {
    success: true;
    date: {
        value: string;
        text: string;
    };
}|{
    success: false;
    errors: FormErrors;
}

export class Request {
    public static async updateRequest(id: number, formData: FormData): Promise<FormErrors | null> {
        const response = await fetch(`/api/request/update?id=${id}`, {
            method: "POST",
            body: formData,
        });

        if (!response.ok) {
            throw new Error(`Request update is failed: ${response.statusText}`);
        }

        return await response.json();
    }

    public static async deleteRequest(id: number): Promise<void> {
        const response = await fetch(`/api/request/delete?id=${id}`, {
            method: "DELETE",
        });

        if (!response.ok) {
            const data = await response.json();

            throw new Error(data.error);
        }
    }

    public static async getSummary(formData: FormData): Promise<RequestSummary | null> {
        const response = await fetch("/api/request/summary", {
            method: "POST",
            body: formData,
        });

        if (!response.ok) {
            console.error(`Request summary is failed: ${response.statusText}`);
            return null;
        }

        return await response.json();
    }

    public static async handleStep(baseUrl: string|null, params: HandleStepParams, formData: FormData): Promise<HandleStepResponse> {
        const queryParams = new URLSearchParams();
        for (const [key, value] of Object.entries(params)) {
            if (value) {
                queryParams.append(key, value.toString());
            }
        }
        const queryString = queryParams.toString();

        let path = "/api/request/handle-step";
        let url = baseUrl !== null ? `${baseUrl}${path}` : path;
        if (queryString) {
            url += `?${queryString}`;
        }

        const response = await fetch(url, {
            method: "POST",
            body: formData,
        });

        if (!response.ok) {
            throw new Error(`Request handle step is failed: ${response.statusText}`);
        }

        return await response.json();
    }

    public static async getForms(): Promise<RequestFormsResponse> {
        const url = new URL("/api/request/forms", window.location.origin);

        const response = await fetch(url.toString(), {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                translationModel,
            }),
        });

        if (!response.ok) {
            throw new Error(`Forms getting is failed: ${response.statusText}`);
        }

        return await response.json();
    }

    public static async prepare(formData: FormData, promo?: Promo, isNavigatorRequest: boolean = false): Promise<any> {
        const url = new URL("/api/request/prepare", window.location.origin);
        const params = new URLSearchParams();

        if (promo) {
            params.append("promo", promo.toString());
        }

        if (isNavigatorRequest) {
            params.append("isNavigatorRequest", "true");
        }

        url.search = params.toString();

        if (!formData.has("uuid")) {
            formData.append("uuid", Analytics.instance().getSessionUuid());
        }

        const response = await fetch(url.toString(), {
            method: "POST",
            body: formData,
        });

        if (!response.ok) {
            throw new HttpError(response.status, `Prepare is failed: ${response.statusText}`);
        }

        return await response.json();
    }

    public static async sendScholarshipRewardRequest(requestId: string|number): Promise<SendScholarshipRewardRequest> {
        const url = new URL(`/api/request/send-scholarship-reward-request?requestId=${requestId}`, window.location.origin);

        const response = await fetch(url.toString(), {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error(`Sending of scholarship reward request is failed: ${response.statusText}`);
        }

        return await response.json();
    }
}